import React from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useChild } from '../contexts/ChildContext';
import { UserRole } from '../types';
import BrandLogo from './BrandLogo';

interface MenuItem {
  label: string;
  path: string;
  color: string;
  icon: string;
  section: 'main' | 'tools' | 'bottom';
}

// Bireysel öğrenci (kitap kodu ile kayıt) için gizlenecek route'lar — kurum/sınıf
// bağımlı tüm özellikler. Anasayfa + Kütüphanem + Çalışma Planım + Digikoç +
// Performansım + Yardım kalır.
// AP61: 'ogrenci-programi' (Çalışma Planım) listeden çıkarıldı — bireysel
// öğrenci kendi hedeflerini ekleyebildiği için plan sayfası ona da açık.
const INDIVIDUAL_HIDE_PATHS = new Set([
  'siniflarim', 'odevler', 'odevlerim', 'randevularim',
  // 'sinav-videolari' 2026-08'de kaldırıldı — konsept Sınav.Link'e devredildi,
  // sayfa ve route'lar silindi. 'deneme-sinavlari' de 2026-09-30'da her istemciden
  // kalktı (uçlar backend'de duruyor).
  'kutuphane-rezervasyon',
]);

function getMenuItems(role: UserRole, basePath: string, isIndividual: boolean): MenuItem[] {
  // Sidebar icons sourced from Figma file 6nrQCBxlgi5ODYgeroTZk8 (SideBar
  // component 28-1924). The legacy /i-*.png placeholders are still in
  // public/ but no longer wired up here.
  if (role === 'teacher') {
    return [
      { label: 'Anasayfa', path: basePath, color: '#09adff', icon: '/sidebar-icons/anasayfa.svg', section: 'main' },
      { label: 'Kurumlarım', path: `${basePath}/kurumlarim`, color: '#f5d316', icon: '/sidebar-icons/kurumlarim.svg', section: 'main' },
      { label: 'Sınıflarım', path: `${basePath}/siniflarim`, color: '#3973bf', icon: '/sidebar-icons/siniflarim.svg', section: 'main' },
      { label: 'Kütüphanem', path: `${basePath}/kutuphanem`, color: '#00c6af', icon: '/sidebar-icons/kutuphanem.svg', section: 'main' },
      { label: 'Ödevler', path: `${basePath}/odevlerim`, color: '#f87aff', icon: '/sidebar-icons/odevlerim.svg', section: 'main' },
      { label: 'Randevularım', path: `${basePath}/randevularim`, color: '#6d399a', icon: '/sidebar-icons/randevularim.svg', section: 'main' },
      { label: 'Kütüphane Görevi', path: `${basePath}/kutuphane-saati`, color: '#01cfd6', icon: '/sidebar-icons/kutuphanem.svg', section: 'main' },
      { label: 'Digikoç', path: `${basePath}/digikoc`, color: '#59e56e', icon: '/sidebar-icons/digikoc.svg', section: 'tools' },
      { label: 'Öğrenci Çalışma Planı', path: `${basePath}/programim`, color: '#6497d2', icon: '/sidebar-icons/calisma-planim.svg', section: 'tools' },
      { label: 'Akıllı Tahta', path: `${basePath}/akilli-tahta`, color: '#01cfd6', icon: '/sidebar-icons/akilli-tahta.svg', section: 'tools' },
      { label: 'Yardım', path: `${basePath}/yardim`, color: '#f1972f', icon: '/sidebar-icons/yardim.svg', section: 'bottom' },
    ];
  }

  if (role === 'student') {
    const all: MenuItem[] = [
      { label: 'Anasayfa', path: basePath, color: '#09adff', icon: '/sidebar-icons/anasayfa.svg', section: 'main' },
      { label: 'Sınıflarım', path: `${basePath}/siniflarim`, color: '#3973bf', icon: '/sidebar-icons/siniflarim.svg', section: 'main' },
      { label: 'Kütüphanem', path: `${basePath}/kutuphanem`, color: '#00c6af', icon: '/sidebar-icons/kutuphanem.svg', section: 'main' },
      { label: 'Ödevlerim', path: `${basePath}/odevler`, color: '#f87aff', icon: '/sidebar-icons/odevlerim.svg', section: 'main' },
      { label: 'Randevularım', path: `${basePath}/randevularim`, color: '#6d399a', icon: '/sidebar-icons/randevularim.svg', section: 'main' },
      { label: 'Kütüphane Rezervasyon', path: `${basePath}/kutuphane-rezervasyon`, color: '#01cfd6', icon: '/sidebar-icons/kutuphanem.svg', section: 'main' },
      { label: 'Digikoç', path: `${basePath}/digikoc`, color: '#59e56e', icon: '/sidebar-icons/digikoc.svg', section: 'tools' },
      { label: 'Çalışma Planım', path: `${basePath}/ogrenci-programi`, color: '#6497d2', icon: '/sidebar-icons/calisma-planim.svg', section: 'tools' },
      { label: 'Performansım', path: `${basePath}/performansim`, color: '#ffda09', icon: '/sidebar-icons/performansim.svg', section: 'tools' },
      { label: 'Yardım', path: `${basePath}/yardim`, color: '#f1972f', icon: '/sidebar-icons/yardim.svg', section: 'bottom' },
    ];
    if (!isIndividual) return all;
    return all.filter((i) => {
      const last = i.path.split('/').pop() ?? '';
      return !INDIVIDUAL_HIDE_PATHS.has(last);
    });
  }

  // Parent
  return [
    { label: 'Anasayfa', path: basePath, color: '#09adff', icon: '/sidebar-icons/anasayfa.svg', section: 'main' },
    { label: 'Öğr. Performansı', path: `${basePath}/performans`, color: '#f5d316', icon: '/sidebar-icons/performansim.svg', section: 'main' },
    { label: 'Öğrenci Programı', path: `${basePath}/ogrenci-programi`, color: '#6497d2', icon: '/sidebar-icons/calisma-planim.svg', section: 'main' },
    { label: 'Öğrenci Ödevleri', path: `${basePath}/ogrenci-odevleri`, color: '#f87aff', icon: '/sidebar-icons/odevlerim.svg', section: 'main' },
    { label: 'Randevularım', path: `${basePath}/randevularim`, color: '#6d399a', icon: '/sidebar-icons/randevularim.svg', section: 'main' },
    { label: 'Yardım', path: `${basePath}/yardim`, color: '#f1972f', icon: '/sidebar-icons/yardim.svg', section: 'bottom' },
  ];
}

interface SidebarProps {
  onClose?: () => void;
}

const Sidebar: React.FC<SidebarProps> = ({ onClose }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  // AP62: velide kurum-bağımlı öğelerin gizlenmesi seçili çocuğa bakar —
  // hook koşulsuz çağrılmalı (erken return'den önce).
  const { children: parentKids, selectedChild } = useChild();

  if (!user) return null;

  const basePath = `/${user.role}`;
  const isIndividual = !!(user as any).is_individual;
  let items = getMenuItems(user.role, basePath, isIndividual);
  // AP62: seçili çocuk bireysel (kurumsuz) ise — ya da hiç seçim yokken tüm
  // çocuklar bireyselse — Öğrenci Ödevleri ve Randevularım gizlenir; ikisi de
  // kurum akışlarıdır (ödevi öğretmen verir, randevu kurum danışmanlığıdır).
  if (user.role === 'parent') {
    const hideKurumItems = selectedChild
      ? selectedChild.isIndividual
      : parentKids.length > 0 && parentKids.every((k) => k.isIndividual);
    if (hideKurumItems) {
      const PARENT_KURUM_PATHS = new Set(['ogrenci-odevleri', 'randevularim']);
      items = items.filter((i) => !PARENT_KURUM_PATHS.has(i.path.split('/').pop() ?? ''));
    }
  }

  const mainItems = items.filter(i => i.section === 'main');
  const toolItems = items.filter(i => i.section === 'tools');
  const bottomItems = items.filter(i => i.section === 'bottom');

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  // Sınav.Link'i yeni sekmede aç. Kullanıcı Birey ile giriş yaptığı için (SSO)
  // orada da otomatik olarak oturum açılır. URL ortam değişkeninden ayarlanabilir.
  // ⚠️ Varsayılan PRODUCTION: değişken hiç set edilmediği için bu satır eskiden
  // staging'e düşüyordu ve production öğrencileri staging Sınav.Link'e (staging
  // IdP'ye, yani hesaplarının olmadığı yere) gidiyordu. ci.yml her ortamda set eder;
  // unutulursa hata staging'de, fark edileceği yerde görünür.
  const openSinavlink = () => {
    const url = process.env.REACT_APP_SINAVLINK_URL || 'https://ekosistemsinavlink.com';
    window.open(`${url}/api/auth/birey/login`, '_blank', 'noopener,noreferrer');
  };

  const renderItem = (item: MenuItem) => (
    <NavLink
      key={item.path}
      to={item.path}
      end={item.path === basePath}
      onClick={onClose}
      className={({ isActive }) =>
        `flex items-center gap-3 h-[42px] xl:h-[48px] px-3 rounded-[10px] transition-all duration-200 bg-[#fafafa] ${
          isActive ? 'shadow-sm border border-gray-200' : 'hover:shadow-sm'
        }`
      }
    >
      <div
        className="w-[30px] h-[30px] xl:w-[34px] xl:h-[34px] rounded-[7px] flex items-center justify-center flex-shrink-0"
        style={{ backgroundColor: item.color }}
      >
        <img src={item.icon} alt="" className="w-[22px] h-[22px] xl:w-[24px] xl:h-[24px] object-contain" />
      </div>
      {/* T31 — long labels ("Kütüphane Rezervasyon", "Öğrenci Çalışma Planı")
          don't fit the 240px rail at the normal size. Drop a step past 16
          chars so the whole word shows; truncate stays as a last-resort guard
          for anything longer still. */}
      <span className={`text-[#2b2b2b] font-semibold truncate ${item.label.length > 16 ? 'text-[11px] xl:text-[13px]' : 'text-[14px] xl:text-[16px]'}`}>{item.label}</span>
    </NavLink>
  );

  return (
    // h-full (viewport-bound via the fixed wrapper in DashboardLayout) — NOT
    // min-h-screen, which lets the column grow past the viewport and pushes
    // Çıkış below the fold with no way to scroll to it.
    <div className="w-[240px] xl:w-[280px] h-full flex flex-col" style={{ background: '#f6f6f6' }}>
      {/* Mobile close button */}
      <button onClick={onClose} className="lg:hidden absolute top-4 right-4 p-2 hover:bg-gray-100 rounded-[8px] z-50">
        <svg width="20" height="20" viewBox="0 0 20 20" fill="#2b2b2b">
          <path d="M4.3 4.3a1 1 0 011.4 0L10 8.59l4.3-4.3a1 1 0 111.4 1.42L11.42 10l4.3 4.3a1 1 0 01-1.42 1.4L10 11.42l-4.3 4.3a1 1 0 01-1.4-1.42L8.58 10 4.3 5.7a1 1 0 010-1.4z"/>
        </svg>
      </button>
      {/* Logo — spaced from left edge */}
      <div className="flex-shrink-0 flex justify-start pl-16 xl:pl-20 pt-5 pb-6 xl:pb-8">
        {/* 36-44px: too short for the "eis ekosis" wordmark to read, so the LMS
            mark alone. The sidebar is #f6f6f6, hence the coloured ink. */}
        <BrandLogo variant="mark" className="h-[36px] xl:h-[44px] object-contain" />
      </div>

      {/* Menu Items — scrolls when the window is short. min-h-0 is required
          for overflow to engage on a flex child (Safari won't shrink it
          below content height otherwise). */}
      <div className="flex-1 min-h-0 overflow-y-auto pl-6 xl:pl-8 pr-4">
        <div className="space-y-1">
          {mainItems.map(renderItem)}
        </div>

        {toolItems.length > 0 && (
          <>
            <div className="my-3 border-t border-gray-100" />
            <div className="space-y-1">
              {toolItems.map(renderItem)}
            </div>
          </>
        )}
      </div>

      {/* Bottom block — pinned to the viewport bottom so Yardım / Sınav.Link /
          Çıkış stay visible at every window height; only the menu above scrolls. */}
      <div className="flex-shrink-0 pl-6 xl:pl-8 pr-4 pb-4">
        <div className="mb-3 border-t border-gray-100" />
        <div className="space-y-1">
          {bottomItems.map(renderItem)}

          {/* Sınav.Link — yeni sekmede otomatik giriş (Birey SSO) */}
          <button
            onClick={openSinavlink}
            className="flex items-center gap-3 h-[42px] xl:h-[48px] px-3 rounded-[10px] w-full hover:bg-blue-50 transition-all duration-200"
          >
            <div className="w-[30px] h-[30px] xl:w-[34px] xl:h-[34px] rounded-[7px] flex items-center justify-center flex-shrink-0 bg-white border border-[#e6e9ef]">
              <img
                src="/sinavlink-icon.svg"
                alt="Sınav.Link"
                className="w-[20px] h-[20px] xl:w-[22px] xl:h-[22px] object-contain"
              />
            </div>
            <span className="text-[#2b2b2b] text-[14px] xl:text-[16px] font-semibold flex items-center gap-1.5">
              Sınav.Link
              <svg
                width="12"
                height="12"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#8a93a2"
                strokeWidth="2.2"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden
              >
                <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
                <path d="M15 3h6v6" />
                <path d="M10 14 21 3" />
              </svg>
            </span>
          </button>

          {/* Logout */}
          <button
            onClick={handleLogout}
            className="flex items-center gap-3 h-[42px] xl:h-[48px] px-3 rounded-[10px] w-full hover:bg-red-50 transition-all duration-200"
          >
            <div className="w-[30px] h-[30px] xl:w-[34px] xl:h-[34px] rounded-[7px] flex items-center justify-center flex-shrink-0 bg-[#d40000]">
              <img src="/sidebar-icons/cikis.svg" alt="" className="w-[18px] h-[18px] xl:w-[20px] xl:h-[20px] object-contain" />
            </div>
            <span className="text-[#2b2b2b] text-[14px] xl:text-[16px] font-semibold">Çıkış</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default Sidebar;
